import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Package, Truck, CheckCircle2, AlertCircle, Sparkles } from 'lucide-react';

interface TerminalRequestCardProps {
  request: any;
  onContinue: (request: any) => void;
  onActivate: (request: any) => void;
  index: number;
}

const STATUS_CONFIG = {
  draft: {
    label: 'טיוטה',
    color: 'gray',
    progress: 10,
    description: 'נדרש תשלום חד פעמי',
    icon: AlertCircle
  },
  payment_completed: {
    label: 'תשלום הושלם',
    color: 'blue',
    progress: 40,
    description: 'נדרש מילוי טופס',
    icon: CheckCircle2
  },
  monthly_pending: {
    label: 'ממתין לתשלום חודשי',
    color: 'amber',
    progress: 60,
    description: 'נדרש תשלום חודשי',
    icon: Clock
  },
  form_pending: {
    label: 'ממתין למילוי טופס',
    color: 'purple',
    progress: 70,
    description: 'נדרש מילוי פרטי מסוף',
    icon: AlertCircle
  },
  waiting_for_shipment: {
    label: 'ממתין למשלוח',
    color: 'blue',
    progress: 85,
    description: 'המסוף בהכנה למשלוח',
    icon: Package
  },
  shipped: {
    label: 'נשלח',
    color: 'green',
    progress: 95,
    description: 'המסוף בדרך אליך',
    icon: Truck
  },
  ready_to_activate: {
    label: 'מוכן להפעלה',
    color: 'green',
    progress: 98,
    description: 'המסוף הגיע - לחץ להפעלה',
    icon: Sparkles
  },
  activated: {
    label: 'הופעל',
    color: 'green',
    progress: 100,
    description: 'המסוף הופעל בהצלחה',
    icon: CheckCircle2
  }
};

export const TerminalRequestCard: React.FC<TerminalRequestCardProps> = ({
  request,
  onContinue,
  onActivate,
  index
}) => {
  const status = STATUS_CONFIG[request.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.draft;
  const Icon = status.icon;

  const getButtonColor = (statusKey: string) => {
    if (statusKey === 'shipped') return 'green';
    return 'blue';
  };

  const buttonColor = getButtonColor(request.status);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      className="bg-white rounded-xl border-2 border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Icon className="h-5 w-5 text-gray-600" />
            <h4 className="font-bold text-[15px] text-gray-900">
              {request.form_data?.contactName || request.terminal_model || 'מסוף סליקה'}
            </h4>
          </div>
          {request.form_data?.regId && (
            <p className="text-[12px] text-gray-500 mb-2 mr-7">
              ח״פ: {request.form_data.regId}
            </p>
          )}
          <span className={`inline-block px-2.5 py-1 bg-${status.color}-100 text-${status.color}-700 text-[11px] font-bold rounded-full`}>
            {status.label}
          </span>
        </div>
      </div>

      <div className="mb-3">
        <div className="flex items-center justify-between text-[12px] text-gray-600 mb-1">
          <span>התקדמות</span>
          <span className="font-semibold">{status.progress}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${status.progress}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className={`h-full bg-gradient-to-r from-${status.color}-500 to-${status.color}-600 rounded-full`}
          />
        </div>
      </div>

      <p className="text-[13px] text-gray-600 mb-3">{status.description}</p>

      {(request.status === 'shipped' || request.status === 'waiting_for_shipment') && (
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onActivate(request)}
          className="w-full py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold text-[14px] hover:shadow-lg hover:shadow-green-200 active:scale-95 transition-all flex items-center justify-center gap-2"
        >
          <Sparkles className="h-4 w-4" />
          קיבלתי את המסוף
        </motion.button>
      )}

      {(request.status === 'draft' ||
        request.status === 'monthly_pending' ||
        request.status === 'form_pending') && (
        <button
          onClick={() => onContinue(request)}
          className={`w-full py-2.5 bg-${buttonColor}-600 text-white rounded-lg font-semibold text-[14px] hover:bg-${buttonColor}-700 active:scale-95 transition-all`}
        >
          המשך
        </button>
      )}
    </motion.div>
  );
};
